import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react'
import { Row, Col } from 'react-bootstrap'
import { useMemberPrograms } from 'hooks/useMemberPrograms'
import {
  AdminTable,
  IconButton,
  InputSearch,
  ProgramEditModal,
  DownloadScholarshipsModal,
  Content,
  Tooltip,
  Permission,
} from 'components'
import styled from 'styled-components'
import {
  FaEye,
  FaEdit,
  FaRegTrashAlt,
  FaPlus,
  FaDownload,
} from 'react-icons/fa'
import { reactLocalStorage } from 'reactjs-localstorage'
import { useCode } from 'hooks/useCode'
import { PROGRAM_STATUSES, YOUR_PROGRAM_STATUSES } from 'utils/constants'
import { I18nContext } from 'i18n'
import { useAlert } from 'react-alert'
import omitEmpty from 'omit-empty'
import { isEqual } from 'lodash'
import DeleteRecord from './DeleteRecord'
import { useProgram } from 'hooks/useProgram'

const { SOURCED, DRAFT, QA_READY, VERIFIED } = PROGRAM_STATUSES

const { UNDER_REVIEW, ACTIVE, INACTIVE } = YOUR_PROGRAM_STATUSES

const ProgramListPage = ({ getStagedCount }) => {
  const {
    dataMemberPrograms,
    loadingMemberPrograms,
    params,
    setParams,
    getMemberPrograms,
  } = useMemberPrograms()

  const {
    dataDetail,
    loadingDetail,
    setProgramId: setDetailId,
    setUpdateProgramId,
    dataUpdate,
    loadingUpdate,
    executeUpdate,
  } = useProgram()

  const { translate } = useContext(I18nContext)

  const { getStatuses, loadingCodes, statuses } = useCode()
  const [data, setData] = useState()
  const [programId, setProgramId] = useState()
  const [mode, setMode] = useState()
  const [show, setShow] = useState(false)
  const [showDownload, setShowDownload] = useState(false)
  const { organization } = reactLocalStorage.getObject('user')
  const alert = useAlert()
  const [showDelete, setShowDelete] = useState(false)

  const pendingTooltipText = useMemo(() => translate('program_changes_pending'), [translate])

  const columns = [
    { name: 'programName', sortable: true, className: 'w-25' },
    { name: 'programSelfDescription', className: 'w-20' },
    { name: 'scholarshipMaximumAward', sortable: true, type: 'Currency', className: 'w-10' },
    { name: 'openDate', type: 'Date', sortable: true, className: 'w-10' },
    { name: 'closeDate', type: 'Date', sortable: true, className: 'w-10' },
    { name: 'dispositionStatus', sortable: true, className: 'w-10' },
    { name: 'lastUpdatedAt', type: 'Date', sortable: true }, // XD-981
    { name: 'changes' },
    { name: 'canEdit', hidden: true },
    { name: 'canDelete', hidden: true },
    { name: 'canView', hidden: true },
    { name: 'id', key: true },
  ]

  const permissions = {
    edit: 'canEditScholarships',
    delete: 'canDeleteScholarships',
  }

  useEffect(() => {
    if (!params) {
      //This function is only executed once
      //Set the default params
      setParams({
        page: 1,
        pageSize: 25,
        orgId: organization?.id,
        staged: true,
        sort: 'programName',
        sortDirection: 'ASC',
      })

      //Get the program statuses
      getStatuses()
    } else {
      const newParams = omitEmpty({
        ...params,
      })

      //Only call the api if the paramas are different
      if (!isEqual(newParams, params) && params) {
        setParams({ ...newParams })
      }
    }
  }, [params])

  useEffect(() => {
    if (dataMemberPrograms?.programs && statuses) {
      //Map the data for the admin table
      const newData = dataMemberPrograms.programs.map(
        ({
          id,
          programName,
          programSelfDescription,
          scholarshipMaximumAward,
          openDate,
          closeDate,
          scholarshipStageStatus,
          statusId,
          lastUpdatedAt,
        }) => {
          // Check if the status should be: Active, Under Review or Inactive
          let statusDesc
          const { name } = statuses.find(({ id }) => id === statusId)
          switch (name) {
            case SOURCED:
            case DRAFT:
            case QA_READY:
              statusDesc = UNDER_REVIEW
              break
            case VERIFIED:
              statusDesc = ACTIVE
              break
            default:
              statusDesc = INACTIVE
              break
          }

          return {
            id,
            programName,
            programSelfDescription,
            scholarshipMaximumAward,
            openDate,
            closeDate,
            //Add Changes Pending in yellow
            dispositionStatus: statusDesc,
            lastUpdatedAt,
            changes: scholarshipStageStatus ? <Tooltip text={pendingTooltipText}><Pending>Changes Pending</Pending></Tooltip> : '',
            //Check if the user can use the actions,
            //reference: https://docs.google.com/presentation/d/1TUmtfDPfXwBDiAGqT713lN2EJRgkxqiZWJow9b-upH0/edit#slide=id.ge5224c94e9_1_0
            canEdit: statusDesc !== INACTIVE && !scholarshipStageStatus,
            canDelete: statusDesc !== INACTIVE && !scholarshipStageStatus,
            canView: true,
          }
        }
      )

      setData(newData)
    }
  }, [dataMemberPrograms, statuses])

  useEffect(() => {
    if (dataUpdate) {
      getMemberPrograms()
      getStagedCount()
    }
  }, [dataUpdate])

  const handleSort = (sort, sortDirection) =>
    setParams({ ...params, sort, sortDirection })

  const handlePageChange = (page, pageSize) =>
    setParams({ ...params, page, pageSize })

  const handleDelete = ({ id }) => {
    setDetailId(id)
    setUpdateProgramId(id)
    setShowDelete(true)
  }

  const handleHide = (message) => {
    setShow(false)
    getMemberPrograms()
    if (message) {
      setTimeout(() => {
        alert.success(message)
        getStagedCount()
      }, 1000)
    }
  }

  const handleViewProgram = ({ id }, mode) => openProgram(mode, id)

  const openProgram = useCallback(
    (action, pId) => {
      setProgramId(pId)
      setMode(action)
      setShow(true)
    },
    [setProgramId, setMode, setShow]
  )

  const handleProgramNameChange = (programName) => {
    if (params) setParams({ ...params, programName, page: 1 })
  }

  const handleDeleteRecord = useCallback(
    (data) => {
      setShowDelete(false)
      executeUpdate({ data })
    },
    [setShowDelete, executeUpdate]
  )

  return (
    <Content>
      <Row>
        <Col lg={3} md={4}>
          <InputSearch
            onChange={handleProgramNameChange}
            placeholder="Filter this List"
            className="tiny ml-3 mb-3"
          />
        </Col>
        <Col className="text-right mr-2">
          <IconButton
            text={translate('program_list_new')}
            icon={<FaPlus />}
            onClick={() => openProgram('edit')}
            className="small"
            tooltip={translate('program_tooltip_new')}
          />
          <IconButton
            icon={<FaDownload />}
            text={translate('program_list_export')}
            onClick={() => setShowDownload(true)}
            className="small"
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <AdminTable
            defaultSort="programName"
            scope="programs"
            server
            onSort={handleSort}
            onPageChange={handlePageChange}
            meta={dataMemberPrograms?.meta}
            loading={
              loadingMemberPrograms ||
              loadingCodes ||
              loadingDetail ||
              loadingUpdate
            }
            customActions={[
              (scholarship) => (
                <IconButton
                  onClick={handleViewProgram.bind(null, scholarship, 'view')}
                  icon={<FaEye />}
                  className="mr-0 pr-0 small"
                  text="View"
                  disabled={!scholarship.canView}
                />
              ),
              (scholarship) => (
                <Permission permission={[permissions?.edit]}>
                  <IconButton
                    onClick={handleViewProgram.bind(null, scholarship, 'edit')}
                    icon={<FaEdit />}
                    className="mr-0 pr-0 small"
                    text="Edit"
                    disabled={!scholarship.canEdit}
                  />
                </Permission>
              ),
              (scholarship) => (
                <Permission permission={[permissions?.delete]}>
                  <IconButton
                    onClick={handleDelete.bind(null, scholarship)}
                    icon={<FaRegTrashAlt />}
                    className="mr-0 pr-0 small"
                    red
                    disabled={!scholarship.canDelete}
                    tooltip={translate('program_tooltip_delete')}
                  />
                </Permission>
              ),

            ]}
            {...{ columns, data }}
          />
        </Col>
      </Row>
      {show && (
        <ProgramEditModal
          show
          onHide={handleHide}
          action={mode}
          toolbar
          items={data}
          id={programId}
          organization={organization}
        />
      )}
      {showDownload && (
        <DownloadScholarshipsModal
          show
          onHide={() => setShowDownload(false)}
          filters={{ programName: params.programName }}
        />
      )}
      {showDelete && dataDetail && !loadingDetail && (
        <DeleteRecord
          onHide={() => setShowDelete(false)}
          program={dataDetail}
          onSubmit={handleDeleteRecord}
          {...{ translate, statuses }}
        />
      )}
    </Content>
  )
}

const Pending = styled.div`
  color: ${({ theme }) => theme.palette.secondary[0]};
  font-size: 0.7rem;
`

export default ProgramListPage
